<template>
  <v-layout v-if="isUserLoggedIn">
    <!-- app bar -->
    <v-app-bar ref="appBar" app dark height="80" color="#333333" style="z-index: 3">
      <v-row align="center">
        <v-col class="col-xs-8 col-sm-5">
          <!-- adminDrawer / back to portal icon -->
          <v-app-bar-nav-icon v-if="isPortalAdmin" @click="adminDrawer = !adminDrawer" class="ml-1"/>

          <!-- app title -->
          <div class="ml-2 px-2 v-btn v-size--default"
               style="cursor: pointer" @click="goToHome()">
            <v-tooltip bottom color="secondary dark ml-2">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs">{{ $t('app.title') }}</span>
              </template>
              <span>{{ $t('common.goToHome') }}</span>
            </v-tooltip>
          </div>
        </v-col>

        <!-- logo -->
        <v-col v-if="$vuetify.breakpoint.smAndUp" cols="2" align="center">
          <v-tooltip bottom color="secondary dark ml-2">
            <template v-slot:activator="{ on, attrs }">
              <img v-on="on" v-bind="attrs" :src="logoSrc" alt="Pulverit" style="cursor: pointer; height: 40px" @click="goToHome()"/>
            </template>
            <span>{{ $t('common.goToHome') }}</span>
          </v-tooltip>
        </v-col>

        <v-col v-if="$vuetify.breakpoint.lgAndUp" cols="5" align="end">
          <!-- locales -->
          <v-menu open-on-hover offset-y transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-avatar v-on="on" v-bind="attrs" :size="20" class="pa-6">
                <img :src="getFlagSrc(`${locale}.svg`)" :alt="locale.text"/>
              </v-avatar>
            </template>
            <v-list>
              <v-list-item v-for="l in locales" :key="l.value" link @click.prevent="updateLanguageSelected(l.value)"
                           :class="{'primary white--text' : locale === l.value}">
                <v-list-item-avatar :size="20"><img :src="getFlagSrc(l.flag)" :alt="l.text"/></v-list-item-avatar>
                <v-list-item-title>{{ l.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- company -->
          <v-menu open-on-hover offset-y transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-on="on" v-bind="attrs" plain small>
                <v-icon class="mr-1" color="primary">mdi-briefcase-variant-outline</v-icon>
                {{ companySelected.companyDescr }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="c in companies" :key="c.idCompany" link @click.prevent="updateCompanySelected(c)"
                           :class="{'primary white--text' : companySelected.idCompany === c.idCompany}">
                <v-list-item-title>{{ c.companyDescr }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- logged user -->
          <v-menu open-on-hover offset-y transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-on="on" v-bind="attrs" plain small>
                <v-icon class="mr-1" color="primary">mdi-account-outline</v-icon>
                {{ userFullName }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item link @click.prevent="logout">
                <v-icon class="mr-2">mdi-logout</v-icon>
                <v-list-item-title>{{ $t('common.logout') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>

        <v-col v-else class="col-xs-2 col-sm-5" align="end">
          <v-app-bar-nav-icon @click.stop="$emit('set-drawer')">
            <v-icon>mdi-backburger</v-icon>
          </v-app-bar-nav-icon>
        </v-col>
      </v-row>
    </v-app-bar>

    <!-- adminDrawer items -->
    <v-navigation-drawer v-if="isPortalAdmin" v-model="adminDrawer" fixed app temporary>
      <div @click="goToHome()" style="height: 80px; background-color: #333333; text-align: center; cursor: pointer" class="pt-4">
        <!-- logo -->
        <img src="@/assets/logo-small.png" alt="Pulverit" style="height: 40px"/>
      </div>

      <v-list nav dense>
        <v-list-item v-for="nav in links" :key="nav.label" @click="routeTo(nav.route)" link>
          <v-icon class="mr-4">{{ nav.icon }}</v-icon>
          <v-list-item-title>{{ nav.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import largeLogo from '@/assets/pulveritWhite.png';
import smallLogo from '@/assets/logo-small.png';

export default {
  name: 'HeaderNav',

  data() {
    return {
      adminDrawer: false,

      links: [
        { icon: 'mdi-home', label: 'Home', route: '/' },
        { icon: 'mdi-account-group', label: 'Utenti', route: '/users' },
        { icon: 'mdi-domain', label: 'Compagnie', route: '/companies' },
        { icon: 'mdi-link-edit', label: 'Link-Compagnie', route: '/links' },
        { icon: 'mdi-set-merge', label: 'Gruppi', route: '/groups' },
        { icon: 'mdi-account-supervisor-circle', label: 'CapoArea-Agente', route: '/agents' },
        { icon: 'mdi-web-sync', label: 'PWS', route: '/pws' }
      ]
    };
  },

  computed: {
    ...mapGetters({
      isUserLoggedIn: 'auth/isUserLoggedIn',
      idUser: 'auth/idUser',
      userFullName: 'auth/userFullName',
      companies: 'auth/companies',
      companySelected: 'auth/companySelected',
      roles: 'auth/roles',
      locale: 'locale/locale',
      locales: 'locale/locales'
    }),

    logoSrc() {
      return this.$vuetify.breakpoint.smAndDown ? smallLogo : largeLogo;
    },

    isPortalAdmin() {
      return this.roles.some(role => role === 'Portal-Admin');
    }
  },

  methods: {
    ...mapActions({
      setLanguageSelected: 'auth/setLanguageSelected',
      setCompanySelected: 'auth/setCompanySelected',
    }),

    goToHome() {
      this.adminDrawer = false;
      this.$router.push('/');
    },

    getFlagSrc(flagFileName) {
      return require(`@/assets/locale/${ flagFileName }`);
    },

    updateLanguageSelected(locale) {
      this.setLanguageSelected({ idUser: this.idUser, locale }).then(() => {
        window.location.reload();
      }).catch((e) => {
        console.error(e);
      });
    },
    updateCompanySelected(company) {
      this.setCompanySelected({ idUser: this.idUser, idCompany: company.idCompany }).then(() => {
        window.location.reload();
      }).catch((e) => {
        console.error(e);
      });
    },

    async logout() {
      await this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    },

    routeTo(route) {
      this.adminDrawer = false;
      this.$router.push(route);
    }
  }
};
</script>
